import { useState, useEffect, useRef, useCallback } from 'react';

export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Returns a debounced version of the callback. The callback will be invoked
 * after `delay` ms of no calls. Call `flush()` to clear any pending invocation.
 */
export function useDebouncedCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  delay: number
): ((...args: A) => void) & { flush: () => void } {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const argsRef = useRef<A | null>(null);

  callbackRef.current = callback;

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (argsRef.current !== null) {
      callbackRef.current(...argsRef.current);
      argsRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args: A) => {
      argsRef.current = args;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        argsRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  ) as ((...args: A) => void) & { flush: () => void };

  debounced.flush = flush;
  return debounced;
}

